import type { Student } from '@mathflat/domain/Student'
import { makeAutoObservable, observable } from 'mobx'

import { localStorageService } from './storage.service'

export type ChildStudent = { studentId: Student['id']; name: string }

// 학부모 전용 상태 (다중 자녀, 활성 자녀 등)
export class ParentRepo {
  private _childStudents: ChildStudent[] = []
  private _activeChildStudentId: Student['id'] | null = null

  constructor() {
    makeAutoObservable<this, '_childStudents' | '_activeChildStudentId'>(this, {
      _childStudents: observable,
      _activeChildStudentId: observable,
    })
  }

  get childStudents() {
    return this._childStudents
  }

  get activeChildStudentId() {
    return this._activeChildStudentId
  }

  get hasMultipleChildren() {
    return this._childStudents.length > 1
  }

  // 현재 활성 자녀의 이름
  get activeChildName() {
    return this._childStudents.find((c) => c.studentId === this._activeChildStudentId)?.name ?? ''
  }

  setChildStudents = (students: ChildStudent[]) => {
    this._childStudents = students

    if (students.length === 0) {
      this._activeChildStudentId = null
      return
    }

    // 활성 자녀가 새 목록에 없으면 첫 번째 자녀로 재선택
    const hasActiveChild =
      this._activeChildStudentId != null &&
      students.some((c) => c.studentId === this._activeChildStudentId)

    if (!hasActiveChild) {
      this.setActiveChildStudentId(students[0].studentId)
    }
  }

  setActiveChildStudentId = (studentId: Student['id']) => {
    // 새로고침 시 localStorage의 stale studentId가 현재 childStudents에 없을 수 있음 — 무효 ID 거부
    const hasChildStudent = this._childStudents.some((child) => child.studentId === studentId)
    if (!hasChildStudent) return

    // localStorage 저장 실패 시 메모리 상태와 어긋나지 않도록 롤백
    const previousStudentId = this._activeChildStudentId
    this._activeChildStudentId = studentId
    try {
      // 자녀 전환 시 localStorage도 갱신 (일부 API가 참조)
      localStorageService.update({ user: { studentId } })
    } catch (err) {
      this._activeChildStudentId = previousStudentId
      throw err
    }
  }
}
